import { HttpErrorResponse } from '@angular/common/http';
import { ApiErrorEnvelope } from './api.models';

export type ApiErrorKind =
  | 'network'
  | 'authentication'
  | 'authorization'
  | 'not-found'
  | 'conflict'
  | 'validation'
  | 'throttled'
  | 'server'
  | 'protocol'
  | 'unknown';

/**
 * One rule a field broke, and what it was measured against.
 *
 * The server sends these beside the English sentences it already sent, from
 * the validator's own record of what failed. They are here so the reason can
 * be written in the reader's language and rewritten when the reader changes
 * it -- a translated sentence from the server could not, because this app
 * switches language without reloading and the message on screen would stay in
 * whichever language it arrived in.
 */
export interface FailedRule {
  readonly rule: string;
  readonly parameters: readonly string[];
}

interface ApiClientErrorOptions {
  status: number;
  kind: ApiErrorKind;
  code?: string;
  requestId?: string;
  fieldErrors?: Readonly<Record<string, readonly string[]>>;
  validationRules?: Readonly<Record<string, readonly string[]>>;
  failedRules?: Readonly<Record<string, readonly FailedRule[]>>;
  conflict?: Readonly<Record<string, unknown>>;
  retryAfterSeconds?: number;
}

/**
 * Safe, transport-independent error exposed to feature services.
 *
 * The raw response body is deliberately not retained because it may contain
 * clinical or financial data that should not be copied into logs or telemetry.
 */
export class ApiClientError extends Error {
  readonly status: number;
  readonly kind: ApiErrorKind;
  readonly code?: string;
  readonly requestId?: string;
  readonly fieldErrors: Readonly<Record<string, readonly string[]>>;
  /** Rule names only; rejected values and rule parameters never belong here. */
  readonly validationRules: Readonly<Record<string, readonly string[]>>;

  /**
   * The rules each field broke, keyed the way the rules are written.
   *
   * Empty for every status but 422, and empty for a 422 from a server that
   * predates this -- the English sentences in `fieldErrors` remain the
   * fallback, so nothing depends on it being there.
   */
  readonly failedRules: Readonly<Record<string, readonly FailedRule[]>>;

  /**
   * The structured body of a 409, and only of a 409.
   *
   * A conflict is not a validation failure: the server is describing a state
   * clash the user has to act on, and the description is structured — the
   * duplicate candidates a registrar must review, and in ADT the admission,
   * bed or encounter the operation collided with. `fieldErrors` cannot carry
   * any of it, because it narrows every entry to an array of strings.
   *
   * Deliberately narrow rather than retaining the whole response: this is
   * clinical data, so it is confined to one status, never logged, and never
   * populated for any other class of failure.
   */
  readonly conflict: Readonly<Record<string, unknown>>;
  readonly retryAfterSeconds?: number;

  constructor(message: string, options: ApiClientErrorOptions) {
    super(message);
    this.name = 'ApiClientError';
    this.status = options.status;
    this.kind = options.kind;
    this.code = options.code;
    this.requestId = options.requestId;
    this.fieldErrors = options.fieldErrors ?? {};
    this.validationRules = options.validationRules ?? {};
    this.failedRules = options.failedRules ?? {};
    this.conflict = options.conflict ?? {};
    this.retryAfterSeconds = options.retryAfterSeconds;
  }

  firstFieldMessage(...fields: string[]): string | null {
    for (const field of fields) {
      const message = this.fieldErrors[field]?.[0];

      if (message) {
        return message;
      }
    }

    return null;
  }
}

/**
 * Writes a refusal in the reader's language, when one has been installed.
 *
 * `normalizeApiError` is a free function, and seventy-four screens call it and
 * show the message it returns. Translating there would mean rewriting all
 * seventy-four to hold the error object and render it -- seventy-four chances
 * to get a catch block wrong -- so instead the i18n layer installs one
 * translator at start-up and every one of those screens says the right thing
 * in the right language without being touched.
 *
 * Deliberately one slot, set once, and only from `ValidationMessages`. It is
 * null in tests that do not boot i18n, and null is handled: the server's own
 * English sentence is the fallback everywhere.
 */
type RefusalTranslator = (
  fieldErrors: Readonly<Record<string, readonly string[]>>,
  failedRules: Readonly<Record<string, readonly FailedRule[]>>,
) => string | null;

let refusalTranslator: RefusalTranslator | null = null;

export function registerRefusalTranslator(translator: RefusalTranslator | null): void {
  refusalTranslator = translator;
}

export function normalizeApiError(error: unknown): ApiClientError {
  if (error instanceof ApiClientError) {
    return error;
  }

  if (!(error instanceof HttpErrorResponse)) {
    return new ApiClientError('The request could not be completed.', {
      status: 0,
      kind: 'unknown',
    });
  }

  const payload = apiErrorEnvelope(error.error);
  const status = error.status;
  const code = responseCode(payload);
  // `errors.code` doubles as a last-resort application-code channel, which is
  // why it used to be stripped from field errors outright. But `code` is also a
  // real column on organizations, branches, departments and cost centres, and
  // discarding it left the operator with a bare "the given data was invalid"
  // and no indication of which input to change.
  //
  // A 422 body's `errors` map is field-keyed by definition — that is what the
  // status means. Application codes ride along with 401/403/419/428 instead, so
  // the status is the honest discriminator.
  const fieldErrors = sanitizeFieldErrors(payload?.errors, status !== 422);
  const validationRules = status === 422
    ? sanitizeValidationRules(payload?.meta?.['validation_rules'])
    : {};
  const failedRules = status === 422 ? sanitizeFailedRules(payload?.meta?.['failed']) : {};
  const conflict = status === 409 && payload?.errors && typeof payload.errors === 'object'
    ? (payload.errors as Readonly<Record<string, unknown>>)
    : {};
  const requestId = safeRequestId(payload?.meta?.['request_id']) ??
    safeRequestId(error.headers.get('X-Request-ID'));
  const retryAfterSeconds = retryAfter(error.headers.get('Retry-After'));
  const kind = errorKind(status);
  const message = status === 422
    ? (refusalTranslator?.(fieldErrors, failedRules) ?? safeMessage(status, payload?.message))
    : safeMessage(status, payload?.message);

  return new ApiClientError(message, {
    status,
    kind,
    ...(code ? { code } : {}),
    ...(requestId ? { requestId } : {}),
    ...(Object.keys(fieldErrors).length > 0 ? { fieldErrors } : {}),
    ...(Object.keys(validationRules).length > 0 ? { validationRules } : {}),
    ...(Object.keys(failedRules).length > 0 ? { failedRules } : {}),
    ...(Object.keys(conflict).length > 0 ? { conflict } : {}),
    ...(retryAfterSeconds !== undefined ? { retryAfterSeconds } : {}),
  });
}

function sanitizeValidationRules(value: unknown): Readonly<Record<string, readonly string[]>> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .filter(([, rules]) => Array.isArray(rules))
      .map(([field, rules]) => [
        field,
        (rules as unknown[]).filter((rule): rule is string =>
          typeof rule === 'string' && /^[a-z][a-z0-9_]{0,63}$/.test(rule)),
      ])
      .filter(([, rules]) => rules.length > 0),
  );
}

function apiErrorEnvelope(value: unknown): ApiErrorEnvelope | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const record = value as Record<string, unknown>;

  return {
    success: false,
    message: typeof record['message'] === 'string' ? record['message'] : '',
    ...(record['errors'] && typeof record['errors'] === 'object'
      ? { errors: record['errors'] as Record<string, string[]> }
      : {}),
    ...(record['meta'] && typeof record['meta'] === 'object'
      ? { meta: record['meta'] as ApiErrorEnvelope['meta'] }
      : {}),
    ...(typeof record['code'] === 'string' ? { code: record['code'] } : {}),
  };
}

function sanitizeFieldErrors(
  errors: Record<string, string[]> | undefined,
  dropCodeChannel: boolean,
): Readonly<Record<string, readonly string[]>> {
  if (!errors) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(errors)
      .filter(([field, messages]) =>
        !(dropCodeChannel && field === 'code') && Array.isArray(messages))
      .map(([field, messages]) => [
        field,
        messages.filter((message): message is string => typeof message === 'string'),
      ])
      .filter(([, messages]) => messages.length > 0),
  );
}

/**
 * Read `meta.failed` without trusting any of it.
 *
 * Every level is checked because this is parsing a response, not reading a
 * type: a rule name that is not a plain identifier is dropped rather than used
 * as a catalogue key, and a parameter that is not a string is dropped rather
 * than interpolated into a sentence a family may read.
 */
function sanitizeFailedRules(value: unknown): Readonly<Record<string, readonly FailedRule[]>> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  const out: Record<string, readonly FailedRule[]> = {};

  for (const [field, rules] of Object.entries(value as Record<string, unknown>)) {
    if (!Array.isArray(rules)) {
      continue;
    }

    const kept = rules
      .filter((entry): entry is Record<string, unknown> =>
        Boolean(entry) && typeof entry === 'object' && !Array.isArray(entry))
      .map((entry) => ({
        rule: typeof entry['rule'] === 'string' ? entry['rule'] : '',
        parameters: Array.isArray(entry['parameters'])
          ? entry['parameters'].filter((p): p is string => typeof p === 'string')
          : [],
      }))
      .filter((entry) => /^[a-z][a-z0-9_]*$/.test(entry.rule));

    if (kept.length > 0) {
      out[field] = kept;
    }
  }

  return out;
}

function responseCode(payload: ApiErrorEnvelope | null): string | undefined {
  const metaCode = payload?.meta?.['code'];
  const errorsCode = payload?.errors?.['code']?.[0];

  return payload?.code ?? (typeof metaCode === 'string' ? metaCode : undefined) ?? errorsCode;
}

function safeRequestId(value: unknown): string | undefined {
  return typeof value === 'string' && /^[A-Za-z0-9._-]{1,128}$/.test(value)
    ? value
    : undefined;
}

function retryAfter(value: string | null): number | undefined {
  if (!value || !/^\d+$/.test(value)) {
    return undefined;
  }

  return Number(value);
}

function errorKind(status: number): ApiErrorKind {
  if (status === 0) return 'network';
  if (status === 401 || status === 419 || status === 428) return 'authentication';
  if (status === 403) return 'authorization';
  if (status === 404) return 'not-found';
  if (status === 409) return 'conflict';
  if (status === 422) return 'validation';
  if (status === 429) return 'throttled';
  if (status >= 500) return 'server';

  return 'unknown';
}

function safeMessage(status: number, serverMessage: string | undefined): string {
  if (status === 0) {
    return 'The hospital server is unavailable.';
  }

  if (status >= 500) {
    return 'The hospital server could not complete the request.';
  }

  return serverMessage?.trim() || 'The request could not be completed.';
}
